import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type {
  ExportChoice,
  ExportLookupKind,
  ExportOptions,
  ExportPreview,
  ExportSelection,
} from "src/types/federation-export";

const base = (providerId: number) =>
  `/providers/${providerId}/federation/exports`;

export function useFederationExportOptions(
  providerId: number,
  enabled: boolean,
) {
  const { user } = useAuthContext();
  return useSWR<ExportOptions>(
    enabled && providerId > 0 && user?.id
      ? `${base(providerId)}/options?schema_version=2#actor-${user.id}`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}

export async function previewFederationExport(
  providerId: number,
  selection: ExportSelection,
  signal?: AbortSignal,
) {
  return (
    await axios.post<ExportPreview>(`${base(providerId)}/preview`, selection, {
      signal,
    })
  ).data;
}

export async function federationExportChoices(
  providerId: number,
  dataset: ExportLookupKind,
  search: string,
  signal: AbortSignal,
  competitionIds: number[] = [],
) {
  const result = (
    await axios.post<{
      items: (ExportChoice | { key: string; label: string })[];
      has_more: boolean;
    }>(
      `${base(providerId)}/filter-options`,
      {
        schema_version: 2,
        dataset,
        search,
        ...(dataset === "categories"
          ? { competition_ids: competitionIds }
          : {}),
      },
      { signal },
    )
  ).data;
  return {
    ...result,
    items: result.items.map(
      (item): ExportChoice =>
        "key" in item ? { id: item.key, label: item.label } : item,
    ),
  };
}

export async function downloadFederationExport(
  providerId: number,
  preview: ExportPreview,
  commandId: string,
  signal?: AbortSignal,
) {
  try {
    return (
      await axios.post<Blob>(
        `${base(providerId)}/download`,
        {
          ...preview.selection,
          preview_proof: preview.preview_proof,
          command_id: commandId,
        },
        { responseType: "blob", signal },
      )
    ).data;
  } catch (error) {
    if (error instanceof Blob) {
      let detail: unknown;
      try {
        detail = JSON.parse(await error.text());
      } catch {
        detail = { detail: "export_unavailable" };
      }
      throw detail;
    }
    throw error;
  }
}

export function exportErrorKey(error: unknown): string {
  const detail = (error as { detail?: string })?.detail;
  if (detail === "export_preview_invalid" || detail === "export_preview_stale")
    return "fx_stale";
  if (detail === "export_row_limit" || detail === "export_byte_limit")
    return "fx_limit_error";
  if (
    detail === "export_permission_denied" ||
    detail?.includes("capability") ||
    detail?.includes("permission")
  )
    return "fx_denied";
  if (detail === "export_input_invalid" || detail === "export_sport_invalid")
    return "fx_invalid";
  return "fx_error";
}
